import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface InfoTooltipProps {
    content: string;
    title?: string;
    className?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({ content, title, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const updatePosition = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPos({
                top: rect.top,
                left: rect.left + rect.width / 2
            });
        }
    }, []);

    useLayoutEffect(() => {
        if (isOpen) {
            updatePosition();
            const handleUpdate = () => updatePosition();
            window.addEventListener('scroll', handleUpdate, true);
            window.addEventListener('resize', handleUpdate);
            return () => {
                window.removeEventListener('scroll', handleUpdate, true);
                window.removeEventListener('resize', handleUpdate);
            };
        }
    }, [isOpen, updatePosition]);

    return (
        <div className={`relative inline-block ${className}`} dir="rtl">
            <button
                ref={triggerRef}
                type="button"
                onMouseEnter={() => setIsOpen(true)}
                onMouseLeave={() => setIsOpen(false)}
                onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(!isOpen);
                }}
                className="text-mystic-400 hover:text-gold-400 transition-colors p-1 cursor-help"
                aria-label="מידע נוסף"
            >
                <Info size={14} />
            </button>

            {isOpen && pos && createPortal(
                <div
                    className="fixed z-[9999] w-64 p-4 glass-card rounded-xl border border-gold-500/30 bg-mystic-900/95 shadow-2xl pointer-events-none"
                    style={{
                        top: pos.top,
                        left: pos.left,
                        transform: 'translate(-50%, -100%)',
                        marginTop: '-12px',
                    }}
                    dir="rtl"
                >
                    {title && <h5 className="text-gold-400 font-bold text-xs mb-1.5 border-b border-white/5 pb-1">{title}</h5>}
                    <p className="text-mystic-200 text-xs leading-relaxed font-normal">
                        {content}
                    </p>
                    {/* Tooltip arrow */}
                    <div
                        className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-mystic-900 border-r border-b border-gold-500/30 transform rotate-45"
                    />
                </div>,
                document.body
            )}
        </div>
    );
};
